import { useNavigate } from 'react-router';

import { PartnerApplicationMachineContext } from '~domains/partner-application/entities/application';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';

import { AddressSuggestionApiProvider } from '~platform/entities/address-suggestion';

import { ampli } from '~shared/analytics';
import { ROUTES } from '~shared/config/routes';

import { BankAccount } from './bank-account';
import { BusinessAddress } from './business-address';
import { BusinessDetails } from './business-details';
import { BusinessDocuments } from './business-documents';
import { BusinessStart } from './business-start';
import { ContinueAirtable } from './continue-airtable';
import { OfficeAddress } from './office-address';
import { Ownership } from './ownership';

import { useApplicationActions } from '../../partner-application';

export const BusinessBlock = () => {
  const navigate = useNavigate();
  const state = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot
  );
  const ownership = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.ownership
  );
  const businessInfo = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.businessInfo
  );
  const registeredAddress = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.registeredAddress
  );
  const headOfficeAddress = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.headOfficeAddress
  );
  const businessDocuments = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.businessDocuments
  );
  const bankDetails = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.bankDetails
  );
  const applicationId = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.applicationId
  );
  const actor = PartnerApplicationMachineContext.useActorRef();
  const { updateApplication, createOrUpdateApplication } =
    useApplicationActions();

  const onClose = () => navigate(ROUTES.partner.applicationMenu);

  const onBack = () => actor.send({ type: 'back' });

  if (!state.matches('businessBlock')) return null;

  return (
    <AddressSuggestionApiProvider>
      {state.matches({ businessBlock: 'businessStart' }) && (
        <BusinessStart
          onSubmit={() => {
            ampli.mpPaBusinessStartScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'next' });
          }}
          onClose={() => {
            ampli.mpPaAboutBusinessScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ businessBlock: 'ownership' }) && (
        <Ownership
          initialValues={ownership}
          onSubmit={async (values) => {
            if (values.type === 'INDIVIDUAL') {
              ampli.mpPaBusinessTypeSoleProprietorSelect(
                getApplicationEventProperties(applicationId)
              );
            } else if (values.type === 'PARTNERSHIP') {
              ampli.mpPaBusinessTypePartnershipSelect(
                getApplicationEventProperties(applicationId)
              );
            } else if (values.type === 'CORPORATION') {
              ampli.mpPaBusinessTypeCorporationSelect(
                getApplicationEventProperties(applicationId)
              );
            }

            ampli.mpPaBusinessTypeScreenNextClick(
              getApplicationEventProperties(applicationId)
            );

            actor.send({ type: 'ownership.submitData', ownership: values });
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaBusinessTypeScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaBusinessTypeScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ businessBlock: 'continueAirtable' }) && (
        <ContinueAirtable
          onBack={() => {
            ampli.mpPaAirtableScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaAirtableScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ businessBlock: 'businessDetails' }) && (
        <BusinessDetails
          initialValues={businessInfo}
          onSubmit={async (values) => {
            actor.send({
              type: 'businessDetails.submitData',
              businessInfo: values,
            });
            await createOrUpdateApplication();
            ampli.mpPaBusinessDetailsScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaBusinessDetailsScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaBusinessDetailsScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ businessBlock: 'businessAddress' }) && (
        <BusinessAddress
          initialValues={registeredAddress}
          onSubmit={async (values) => {
            ampli.mpPaRegisteredBusinessAddressScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({
              type: 'businessAddress.submitData',
              registeredAddress: values,
            });
            await updateApplication();
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaRegisteredBusinessAddressScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaRegisteredBusinessAddressScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ businessBlock: 'officeAddress' }) && (
        <OfficeAddress
          initialValues={headOfficeAddress}
          businessAddress={registeredAddress}
          onSubmit={async (values) => {
            ampli.mpPaMainOfficeAddressScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({
              type: 'officeAddress.submitData',
              headOfficeAddress: values,
            });
            await updateApplication();
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaMainOfficeAddressScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaMainOfficeAddressScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ businessBlock: 'businessDocuments' }) && (
        <BusinessDocuments
          applicationId={applicationId}
          initialValues={businessDocuments}
          onSubmit={async (values) => {
            ampli.mpPaBusinessDocumentsScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({
              type: 'businessDocuments.submitData',
              businessDocuments: values,
            });
            await updateApplication();
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaBusinessDocumentsScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaBusinessDocumentsScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ businessBlock: 'bankDetails' }) && (
        <BankAccount
          applicationId={applicationId}
          initialValues={bankDetails}
          onSubmit={async (values) => {
            ampli.mpPaBankingDetailsScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'bankDetails.submitData', bankDetails: values });
            await updateApplication();
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaBankingDetailsScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaBankingDetailsScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
    </AddressSuggestionApiProvider>
  );
};
